import { Box, Divider, Paper, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNotification } from "../../../../componentes/notificationProvider";
import NewAlertModal from "../../../../componentes/newAlertModal";
import FlowHeader from "../components/flowHeader";
import FlowTable from "../components/flowTable";
import { useTabStyles } from "../components/styles";
import ModalInterface from "./modalInterface";
import MassInterfaces from "./massInterfaces";
import { interfaceColumns } from "./columns";

import { InterfaceFlow, InterfacePayload } from "./types";
import { Router } from "../routers/types";
import api from "../../../../services/api";
import { AxiosError } from "axios";

export default function Interfaces() {
  const [search, setSearch] = useState<string>("");
  const [interfaces, setInterfaces] = useState<InterfaceFlow[]>([]);
  const [exportadorOptions, setExportadorOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isMassMode, setIsMassMode] = useState<boolean>(false);
  const [tableKey, setTableKey] = useState<number>(0);

  const [openInterfaceModal, setOpenInterfaceModal] = useState<boolean>(false);
  const [editingInterface, setEditingInterface] =
    useState<InterfaceFlow | null>(null);
  const [savingInterface, setSavingInterface] = useState<boolean>(false);

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [selectedInterface, setSelectedInterface] =
    useState<InterfaceFlow | null>(null);

  const theme = useTheme();
  const styles = useTabStyles(theme);
  const notify = useNotification();

  useEffect(() => {
    getInterfaces();
    getExportadores();
  }, []);

  const getInterfaces = async () => {
    try {
      setLoading(true);

      const response = await api.get("/easyFlow2.0/interfaces");

      setInterfaces(
        Array.isArray(response.data?.data) ? response.data.data : [],
      );
      setTableKey((key) => key + 1);
    } catch (error: any) {
      console.error(error);
      notify(
        `Error:${
          error?.message || "Error: Não foi possível se conectar com o servidor"
        } `,
        "error",
        6000,
      );
    } finally {
      setLoading(false);
    }
  };

  const getExportadores = async () => {
    try {
      const response = await api.get("/easyFlow2.0/routers");
      const routers: Router[] = Array.isArray(response.data?.data)
        ? response.data.data
        : [];

      setExportadorOptions(
        routers.map((router) => router.name).filter((name) => Boolean(name)),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const rowsForDisplay = useMemo(() => {
    if (!search.trim()) return interfaces;

    const searchLower = search.toLowerCase();
    return interfaces.filter((interfaceItem) =>
      Object.values(interfaceItem).some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(searchLower),
      ),
    );
  }, [interfaces, search]);

  const handleOpenCreate = () => {
    setEditingInterface(null);
    setOpenInterfaceModal(true);
  };

  const handleEditRow = (interfaceItem: InterfaceFlow) => {
    setEditingInterface(interfaceItem);
    setOpenInterfaceModal(true);
  };

  const handleSaveInterface = async (
    payload: InterfacePayload,
    id?: number,
  ) => {
    try {
      setSavingInterface(true);

      if (id) {
        await api.put(`/easyFlow2.0/interfaces/${id}`, {
          ...payload,
        });
        notify("Interface atualizada com sucesso", "success");
      } else {
        await api.post(`/easyFlow2.0/interfaces`, {
          ...payload,
        });
        notify("Interface criada com sucesso", "success");
      }

      setOpenInterfaceModal(false);
      setEditingInterface(null);
      await getInterfaces();
    } catch (error: any) {
      console.error(error);
      notify(
        error?.response?.data?.message ||
          error?.message ||
          "Não foi possível salvar a interface",
        "error",
      );
    } finally {
      setSavingInterface(false);
    }
  };

  const handleDeleteRow = (interfaceItem: InterfaceFlow) => {
    setSelectedInterface(interfaceItem);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedInterface) return;
    try {
      await api.delete(`/easyFlow2.0/interfaces/${selectedInterface.id}`);
      notify("Interface removida com sucesso!", "success", 3000);
      await getInterfaces();
    } catch (error) {
      if (error instanceof AxiosError)
        notify(
          error.response?.data?.error || "Erro ao remover interface",
          "error",
          3000,
        );
      else notify("Não foi possível se conectar ao servidor!", "error", 3000);
    } finally {
      setOpenDeleteModal(false);
      setSelectedInterface(null);
    }
  };

  return (
    <Paper elevation={0} sx={styles.paper}>
      {isMassMode ? (
        <MassInterfaces
          interfaces={interfaces}
          exportadorOptions={exportadorOptions}
          search={search}
          onSearchChange={setSearch}
          onBack={() => setIsMassMode(false)}
          onSaved={getInterfaces}
        />
      ) : (
        <>
          <FlowHeader
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Pesquisar interfaces"
            onEnterEditMode={() => setIsMassMode(true)}
            disableEditMode={loading}
            onAdd={handleOpenCreate}
            onRefresh={getInterfaces}
            loading={loading}
          />
          <Divider
            flexItem
            sx={{ my: 1, borderColor: theme.palette.divider }}
          />
          <Box sx={styles.boxTable}>
            <FlowTable<InterfaceFlow>
              key={`interfaces-${tableKey}`}
              id="interfacesFlow"
              rows={rowsForDisplay}
              columns={interfaceColumns}
              loadingTable={loading}
              onEditRow={handleEditRow}
              onDeleteRow={handleDeleteRow}
            />
          </Box>
        </>
      )}

      <ModalInterface
        open={openInterfaceModal}
        loading={savingInterface}
        interfaceData={editingInterface}
        exportadorOptions={exportadorOptions}
        onClose={() => {
          setOpenInterfaceModal(false);
          setEditingInterface(null);
        }}
        onSave={handleSaveInterface}
      />

      <NewAlertModal
        openModal={openDeleteModal}
        setOpenModal={setOpenDeleteModal}
        textContent={`Tem certeza que deseja excluir a interface ${
          selectedInterface?.interface || selectedInterface?.exportador || ""
        }?`}
        handleButton={handleConfirmDelete}
        handleCancel={() => setSelectedInterface(null)}
        severity={"error"}
        singleButton={false}
      />
    </Paper>
  );
}
